import { getCurrentStorageUser } from '../common/localStorage/currentStorageUser'
import { reportCaughtError } from '@features/_common/errorHandler'

export const STORAGE_BUDGET_BYTES = 5 * 1024 * 1024
export const STORAGE_WARN_BYTES = Math.floor(3.5 * 1024 * 1024)

const debouncedPayloads = new Map()
const debouncedTimers = new Map()

export const piniaStorageKey = () => getCurrentStorageUser() + '@pinia'

export const isQuotaError = (error) => {
    if (!error) return false
    const message = error.message || ''
    return error.name === 'QuotaExceededError' || message.includes('quota')
}

export const jsonBytes = (value) => {
    try {
        return new Blob([JSON.stringify(value ?? null)]).size
    } catch (e) {
        return 0
    }
}

export const questionIsFat = (question) => {
    if (!question || typeof question !== 'object') return false
    return Object.keys(question).some((key) => key !== 'id' && key !== 'marked')
}

export const slimPersistedAnswer = (answer) => {
    if (!answer || typeof answer !== 'object') return answer
    const question = answer.question
    if (!question || typeof question !== 'object') return answer
    return {
        ...answer,
        question: { id: question.id, marked: !!question.marked },
    }
}

export const readPiniaBlob = () => {
    try {
        const stored = localStorage.getItem(piniaStorageKey())
        if (!stored) return {}
        const data = JSON.parse(stored)
        return data && typeof data === 'object' && !Array.isArray(data) ? data : {}
    } catch (e) {
        reportCaughtError(e, 'persist.read')
        return {}
    }
}

export const writePiniaBlob = (data) => {
    try {
        localStorage.setItem(piniaStorageKey(), JSON.stringify(data))
        return true
    } catch (e) {
        reportCaughtError(e, isQuotaError(e) ? 'persist.quota' : 'persist.write')
        return false
    }
}

export const piniaBlobBytes = () => {
    try {
        const stored = localStorage.getItem(piniaStorageKey()) || ''
        return new Blob([stored]).size
    } catch (e) {
        return 0
    }
}

const sliceFieldBytes = (slice, field) => {
    if (!slice || slice[field] === undefined) return 0
    return jsonBytes(slice[field])
}

export const refetchableSliceBytes = () => {
    const blob = readPiniaBlob()
    return (
        sliceFieldBytes(blob.questionsStore, 'allQuestions') +
        sliceFieldBytes(blob.questionsStore, 'currentQuestions') +
        sliceFieldBytes(blob.questionsStore, 'unfilteredCurrentQuestions') +
        sliceFieldBytes(blob.mainCategoriesStore, 'allMainCategories') +
        sliceFieldBytes(blob.subCategoriesStore, 'allLoadedSubCategories') +
        sliceFieldBytes(blob.subCategoriesStore, 'allSubCategories') +
        sliceFieldBytes(blob.bookmarksStore, 'bookmarksList') +
        sliceFieldBytes(blob.testTemplateStore, 'allTestQuestions')
    )
}

export const blobHasStrippableFat = (blob) => {
    const questions = blob.questionsStore
    const mains = blob.mainCategoriesStore
    const subs = blob.subCategoriesStore
    const bookmarks = blob.bookmarksStore
    const template = blob.testTemplateStore
    const answers = blob.answersStore
    if (Array.isArray(questions?.allQuestions) && questions.allQuestions.length > 0) return true
    if (Array.isArray(questions?.currentQuestions) && questions.currentQuestions.length > 0) return true
    if (Array.isArray(questions?.unfilteredCurrentQuestions) && questions.unfilteredCurrentQuestions.length > 0) return true
    if (Array.isArray(mains?.allMainCategories) && mains.allMainCategories.length > 0) return true
    if (Array.isArray(subs?.allLoadedSubCategories) && subs.allLoadedSubCategories.length > 0) return true
    if (Array.isArray(subs?.allSubCategories) && subs.allSubCategories.length > 0) return true
    if (Array.isArray(bookmarks?.bookmarksList) && bookmarks.bookmarksList.length > 0) return true
    if (Array.isArray(template?.allTestQuestions) && template.allTestQuestions.length > 0) return true
    if (Array.isArray(answers?.userAnswers) && answers.userAnswers.some((answer) => questionIsFat(answer?.question))) return true
    return false
}

export const stripFatFromBlob = (blob) => {
    if (blob.questionsStore) {
        delete blob.questionsStore.allQuestions
        delete blob.questionsStore.currentQuestions
        delete blob.questionsStore.unfilteredCurrentQuestions
    }
    if (blob.mainCategoriesStore) {
        delete blob.mainCategoriesStore.allMainCategories
    }
    if (blob.subCategoriesStore) {
        delete blob.subCategoriesStore.allLoadedSubCategories
        delete blob.subCategoriesStore.allSubCategories
    }
    if (blob.bookmarksStore) {
        delete blob.bookmarksStore.bookmarksList
    }
    if (blob.testTemplateStore) {
        delete blob.testTemplateStore.allTestQuestions
    }
    if (Array.isArray(blob.answersStore?.userAnswers)) {
        blob.answersStore.userAnswers = blob.answersStore.userAnswers.map(slimPersistedAnswer)
    }
    return blob
}

export const extractFatSession = (blob) => {
    const template = blob.testTemplateStore || {}
    const answers = blob.answersStore || {}
    const mains = blob.mainCategoriesStore || {}
    const subs = blob.subCategoriesStore || {}
    const allTestQuestions = Array.isArray(template.allTestQuestions) ? template.allTestQuestions : []
    const userAnswers = Array.isArray(answers.userAnswers) ? answers.userAnswers : []
    const allMainCategories = Array.isArray(mains.allMainCategories) ? mains.allMainCategories : []
    const allLoadedSubCategories = Array.isArray(subs.allLoadedSubCategories) ? subs.allLoadedSubCategories : []
    const hasFatAnswers = userAnswers.some((answer) => questionIsFat(answer?.question))
    if (
        allTestQuestions.length === 0 &&
        !hasFatAnswers &&
        allMainCategories.length === 0 &&
        allLoadedSubCategories.length === 0
    ) {
        return null
    }
    return {
        allTestQuestions,
        userAnswers,
        allMainCategories,
        selectedCategory: mains.selectedCategory,
        selectedCategories: mains.selectedCategories,
        allLoadedSubCategories,
        allSubCategories: subs.allSubCategories,
        selectedSubCategories: subs.selectedSubCategories,
        selectedSubCategory: subs.selectedSubCategory,
    }
}

const cancelDebounced = (sliceName) => {
    const timer = debouncedTimers.get(sliceName)
    if (timer) clearTimeout(timer)
    debouncedTimers.delete(sliceName)
    debouncedPayloads.delete(sliceName)
}

const writeSlice = (sliceName, payload) => {
    try {
        const data = readPiniaBlob()
        data[sliceName] = payload
        writePiniaBlob(data)
    } catch (e) {
        reportCaughtError(e, isQuotaError(e) ? 'persist.quota' : 'persist.write-slice', { sliceName })
    }
}

export const persistPiniaSlice = (sliceName, payload) => {
    cancelDebounced(sliceName)
    writeSlice(sliceName, payload)
}

export const persistPiniaSliceDebounced = (sliceName, payload, delay = 400) => {
    debouncedPayloads.set(sliceName, payload)
    const existing = debouncedTimers.get(sliceName)
    if (existing) clearTimeout(existing)
    debouncedTimers.set(sliceName, setTimeout(() => {
        debouncedTimers.delete(sliceName)
        const latest = debouncedPayloads.get(sliceName)
        debouncedPayloads.delete(sliceName)
        if (latest) writeSlice(sliceName, latest)
    }, delay))
}

export const flushDebouncedPiniaPersists = () => {
    for (const [sliceName, payload] of debouncedPayloads) {
        const timer = debouncedTimers.get(sliceName)
        if (timer) clearTimeout(timer)
        writeSlice(sliceName, payload)
    }
    debouncedTimers.clear()
    debouncedPayloads.clear()
}

export const loadPiniaSlice = (sliceName) => {
    try {
        const data = readPiniaBlob()
        return data[sliceName] || null
    } catch (e) {
        reportCaughtError(e, 'persist.load', { sliceName })
        return null
    }
}

export const dropRefetchablePiniaSlices = () => {
    const before = piniaBlobBytes()
    const blob = stripFatFromBlob(readPiniaBlob())
    writePiniaBlob(blob)
    return { before, after: piniaBlobBytes() }
}

export const assessStorageForTest = (questionData) => {
    const used = piniaBlobBytes()
    const questionBytes = jsonBytes(questionData)
    const refetchable = refetchableSliceBytes()
    const projected = used + questionBytes
    const blobFull = used > STORAGE_WARN_BYTES
    const overBudget = projected > STORAGE_BUDGET_BYTES
    const cleanupHelps = refetchable > 64 * 1024
    return {
        used,
        questionBytes,
        refetchable,
        tight: blobFull || (overBudget && cleanupHelps),
    }
}

if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
        flushDebouncedPiniaPersists()
    })
}
